import { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";
import MDSocialButton from "components/MDSocialButton";

import FacebookIcon from "@mui/icons-material/Facebook";
import GoogleIcon from "components/auth/GoogleIcon";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import CoverLayout from "components/CoverLayout";
import bgImage from "assets/images/bg-sign-up-cover.jpeg";

import { LanguageContext } from "context";
import { supabase } from "lib/supabaseClient";

import { registAccount } from "@shared/api/auth";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

function SignUp(): JSX.Element {
  const { t, language, setLanguage } = useContext(LanguageContext) as any;
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [agreed, setAgreed] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const [agreeError, setAgreeError] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [passwordConfirmError, setPasswordConfirmError] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");
  const [alertColor, setAlertColor] = useState<"info" | "error">("info");
  const [showAlert, setShowAlert] = useState(false);

  function getCookie(name: string): string | null {
    const cookies = document.cookie.split("; ");
    const cookie = cookies.find((c) => c.startsWith(`${name}=`));
    return cookie ? decodeURIComponent(cookie.split("=")[1]) : null;
  }

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const hasName = name.trim() !== "";
    const hasEmail = email.trim() !== "";

    setNameError(!hasName);
    setEmailError(!hasEmail);
    setPasswordError(password.trim().length < 8);
    setPasswordConfirmError(password !== passwordConfirm || passwordConfirm.trim() === "");

    if (!hasName || !hasEmail) {
      setAlertColor("error");
      setAlertMessage(t("sign-up.alert_req_fields") || "名前・Eメール・パスワードをすべて入力してください。");
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);
      setIsSubmitting(false);
      return;
    }

    if (password.trim().length < 8) {
      setAlertColor("error");
      setAlertMessage(t("sign-up.alert_pw_length") || "パスワードは8文字以上で入力してください。");
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);
      setIsSubmitting(false);
      return;
    }

    if (password !== passwordConfirm) {
      setAlertColor("error");
      setAlertMessage(t("sign-up.alert_pw_match") || "パスワードが一致しません。");
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);
      setIsSubmitting(false);
      return;
    }

    if (!agreed) {
      setAgreeError(true);
      setAlertColor("error");
      setAlertMessage(t("sign-up.alert_req_terms") || "利用規約に同意してください。");
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);
      setIsSubmitting(false);
      return;
    }

    const redirectUrl = import.meta.env.DEV
      ? "https://front-chrono.radiant-ma.com/auth/callback" // ローカル（開発）環境
      : "https://ai.chronospark.jp/auth/callback";    // 本番環境

    const selectedLanguage = language === "en" ? "英語" : "日本語";

    // Supabaseにユーザー登録
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name, lang: selectedLanguage },
        emailRedirectTo: redirectUrl,
      },
    });

    if (error || !data?.user) {
      console.error("登録エラー:", error?.message);
      setAlertColor("error");
      setAlertMessage(t("sign-up.alert_fail_msg") || "登録に失敗しました。すでに存在するEメールの可能性があります。");
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);
      setIsSubmitting(false);
      return;
    }

    try {
      // バックエンドAPIでuser_infoにも保存
      await registAccount(data.user.id, email, name, selectedLanguage);

      // 確認メッセージとログイン画面遷移
      setAlertColor("info");
      setAlertMessage(t("sign-up.alert_success_msg"));
      setShowAlert(false);
      setTimeout(() => {
        setShowAlert(true);
        setIsSubmitting(false);
      }, 0);
    } catch (err) {
      console.error("DB登録エラー:", err);
      setAlertColor("error");
      setAlertMessage(t("sign-up.alert_fail_msg") || "ユーザー情報の保存に失敗しました。");
      setShowAlert(false);
      setTimeout(() => setShowAlert(true), 0);
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const isFirstLogin = getCookie("googleLoginDone") !== "true";
      const silentParam = isFirstLogin ? "false" : "true";

      window.location.href = `${API_BASE}/api/auth-google/google-login?silent=${silentParam}`;
    } catch (err) {
      console.error("ログインに失敗:", err);
    }
  };

  const handleFacebookLogin = async () => {
   try {
      const isFirstLogin = getCookie("facebookLoginDone") !== "true";
      const silentParam = isFirstLogin ? "false" : "true";

      window.location.href = `${API_BASE}/api/auth-facebook/facebook-login?silent=${silentParam}`;
    } catch (err) {
      console.error("ログインに失敗:", err);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const togglePasswordConfirmVisibility = () => setShowPasswordConfirm(!showPasswordConfirm);

  return (
    <CoverLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="success"
          borderRadius="lg"
          coloredShadow="success"
          mx={2}
          mt={-3}
          p={3}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            {t("sign-up.Join_us_today")}
          </MDTypography>
          <MDTypography display="block" variant="button" color="white" my={1}>
            {t("sign-up.Enter_your")}
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          {showAlert && (
            <MDBox mb={2}>
              <MDAlert color={alertColor} dismissible onClose={() => setShowAlert(false)}>
                {alertMessage}
              </MDAlert>
            </MDBox>
          )}

          <MDBox display="flex" justifyContent="flex-end" mb={2} gap={1}>
            <MDButton
              variant={language === "ja" ? "contained" : "outlined"}
              color="info"
              size="small"
              onClick={() => setLanguage?.("ja")}
              sx={{ px: 2, py: 0.5, fontSize: "0.75rem" }}
            >
              日本語
            </MDButton>
            <MDButton
              variant={language === "en" ? "contained" : "outlined"}
              color="info"
              size="small"
              onClick={() => setLanguage?.("en")}
              sx={{ px: 2, py: 0.5, fontSize: "0.75rem" }}
            >
              English
            </MDButton>
          </MDBox>

          <MDBox component="form" role="form" onSubmit={(e) => e.preventDefault()}>
            <MDBox mb={2}>
              <MDInput
                type="text"
                label={t("sign-up.Name") || "名前"}
                variant="standard"
                fullWidth
                value={name}
                onChange={(e: any) => setName(e.target.value)}
                error={nameError}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="email"
                label={t("sign-up.Email") || "Eメール"}
                variant="standard"
                fullWidth
                value={email}
                onChange={(e: any) => setEmail(e.target.value)}
                error={emailError}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type={showPassword ? "text" : "password"}
                label={t("sign-up.Password") || "パスワード"}
                variant="standard"
                fullWidth
                value={password}
                onChange={(e: any) => setPassword(e.target.value)}
                error={passwordError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={togglePasswordVisibility} edge="end">
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type={showPasswordConfirm ? "text" : "password"}
                label={t("sign-up.password_confirm") || "パスワード（確認用）"}
                variant="standard"
                fullWidth
                value={passwordConfirm}
                onChange={(e: any) => setPasswordConfirm(e.target.value)}
                error={passwordConfirmError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={togglePasswordConfirmVisibility} edge="end">
                        {showPasswordConfirm ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>
            <MDBox display="flex" alignItems="center" ml={-1}>
              <Checkbox
                checked={agreed}
                onChange={() => {
                  setAgreed(!agreed);
                  setAgreeError(false);
                }}
              />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color={agreeError ? "error" : "text"}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;{t("sign-up.I_agree_the")}&nbsp;
              </MDTypography>
              <MDTypography
                component="a"
                href="https://chronospark.jp/terms/"
                target="_blank"
                rel="noreferrer"// セキュリティ対策
                variant="button"
                fontWeight="bold"
                color="info"
                textGradient
              >
                {t("sign-up.Terms_and_Conditions")}
              </MDTypography>
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton
                variant="gradient"
                color="success"
                fullWidth
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("sign-up.register") || t("sign-up.sign_in")
                )}
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                {t("sign-up.Already_have_an_account")}{" "}
                <MDTypography
                  component={Link}
                  to="/pages/auth/Sign-In"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  {t("sign-up.Sign_In")}
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </CoverLayout>
  );
}

export default SignUp;
